import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  LayoutDashboard, Images, CalendarDays, FileText, CreditCard, MessageSquare,
  Send, CheckCircle2, Clock, Circle, CalendarClock, PlayCircle, LogOut, ArrowLeft,
  Download, ShieldCheck, ScrollText, ReceiptText, FileSignature, X, KeyRound, MapPin,
} from "lucide-react";

const LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_a2603301-837e-467e-952a-d7e2edb4c197/artifacts/3ut7qksg_file_0000000043b081f5a40b26af34420fa3.webp";
const PORTAL_TOKEN_KEY = "gi_portal_token";
const PORTAL_CODE_KEY = "gi_portal_code";

const errText = (d, f) => (typeof d === "string" ? d : f);
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Silently exchange a saved code for a fresh token. Returns new token or null. */
async function silentReauth() {
  const code = localStorage.getItem(PORTAL_CODE_KEY);
  if (!code) return null;
  try {
    const { data } = await api.post("/auth/portal-code", { code });
    localStorage.setItem(PORTAL_TOKEN_KEY, data.token);
    return data.token;
  } catch {
    localStorage.removeItem(PORTAL_CODE_KEY);
    return null;
  }
}

/* ================= Code entry screen ================= */
const CodeScreen = ({ onAuthed }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanCode = code.trim().toUpperCase();
      // Wipe any stale credentials before attempting fresh login — prevents mixed state.
      localStorage.removeItem(PORTAL_TOKEN_KEY);
      localStorage.removeItem(PORTAL_CODE_KEY);
      const { data } = await api.post("/auth/portal-code", { code: cleanCode });
      localStorage.setItem(PORTAL_TOKEN_KEY, data.token);
      localStorage.setItem(PORTAL_CODE_KEY, cleanCode);
      toast.success(`Welcome back${data.client_name ? `, ${data.client_name}` : ""}`);
      onAuthed(data.token);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, "Could not open project"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="flex items-center justify-between px-6 md:px-10 h-20 border-b border-white/5">
        <Link to="/" data-testid="portal-home-link" className="flex items-center gap-3 text-white/70 hover:text-white text-sm">
          <ArrowLeft size={16} /> GI Remodeling
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full max-w-xl"
        >
          <img src={LOGO} alt="GI Remodeling" className="h-20 mx-auto object-contain mb-8" />

          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Client Access</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl leading-tight">
              <span className="gold-gradient-text">Welcome</span> to Your Project
            </h1>
            <p className="text-white/60 font-body italic mt-4 max-w-md mx-auto">
              Your dedicated space for every stage of your remodeling project.
            </p>
          </div>

          <div className="mt-10 border-y border-white/10 py-8">
            <p className="text-white/60 font-body text-sm leading-relaxed">
              This secure portal gives you convenient access to everything related to your project in one place. From your proposal to the final walkthrough, you'll be able to stay informed every step of the way.
            </p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold mt-6 mb-4">Inside your project, you can:</p>
            <ul className="space-y-2.5">
              {[
                "View your project proposal and important documents.",
                "Track progress with before, during, and after photos.",
                "Review your project schedule and milestones.",
                "Check your payment summary and balance.",
                "Communicate directly with the GI Remodeling team.",
                "Stay informed with project updates from start to finish.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/70 font-body text-sm leading-relaxed">
                  <span className="mt-2 h-px w-3 bg-gold shrink-0" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-center text-white/60 font-body text-sm mt-8 mb-5">
            Enter your Project Access Code below to continue.
          </p>

          <form data-testid="portal-code-form" onSubmit={submit} className="space-y-5">
            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-gold" size={18} />
              <input
                data-testid="portal-code"
                autoFocus
                required
                placeholder="GI-XXXXXX"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="editorial-input uppercase tracking-widest text-center text-lg pl-12"
                maxLength={12}
              />
            </div>
            <button
              data-testid="portal-submit"
              disabled={loading || !code.trim()}
              className="w-full bg-gold text-black text-sm font-semibold uppercase tracking-widest py-4 hover:bg-white transition-colors disabled:opacity-50"
            >
              {loading ? "Opening…" : "Open my project"}
            </button>
          </form>

          <p className="mt-6 text-white/40 text-xs text-center leading-relaxed">
            Your project code was given to you by GI Remodeling. It looks like{" "}
            <span className="text-gold">GI-A1B2C3</span>. If you don't have one,{" "}
            <Link to="/contact" className="text-gold hover:underline">contact us</Link>.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

/* ================= Shared bits ================= */
const ProgressRing = ({ value = 0, size = 150 }) => {
  const stroke = 8, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke="#d4af37" strokeWidth={stroke} fill="none"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: off }}
          transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif text-4xl gold-gradient-text">{value}%</span>
        <span className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Complete</span>
      </div>
    </div>
  );
};

const STAGE_ICON = { completed: CheckCircle2, in_progress: Clock, pending: Circle };
const STAGE_COLOR = { completed: "text-gold", in_progress: "text-white", pending: "text-white/30" };
const DOC_ICON = { contract: FileSignature, proposal: ScrollText, warranty: ShieldCheck, invoice: ReceiptText };
const STATUS_LABEL = { in_progress: "In Progress", completed: "Completed", on_hold: "On Hold" };

const SectionTitle = ({ children }) => <h2 className="font-serif text-3xl md:text-4xl mb-8">{children}</h2>;
const Empty = ({ children }) => <p className="text-white/40 font-body text-sm">{children}</p>;

/* ---------- Overview ---------- */
const Overview = ({ job, totals }) => (
  <div>
    <SectionTitle>Project Overview</SectionTitle>
    <div className="grid lg:grid-cols-[auto_1fr] gap-10 items-center border border-white/10 bg-[#0a0a0a] p-8">
      <div className="flex justify-center"><ProgressRing value={job.progress || 0} /></div>
      <div>
        <span className="text-[10px] uppercase tracking-[0.25em] border border-gold/40 text-gold px-3 py-1">{STATUS_LABEL[job.status] || job.status}</span>
        {job.description ? (
          <p className="text-white/70 font-body mt-6 max-w-2xl leading-relaxed">{job.description}</p>
        ) : (
          <p className="text-white/40 font-body italic mt-6">Progress details will appear here as your project moves forward.</p>
        )}
      </div>
    </div>
    <div className="grid sm:grid-cols-3 gap-4 mt-4">
      {[
        { l: "Contract Total", v: money(totals.total) },
        { l: "Paid to Date", v: money(totals.paid) },
        { l: "Balance Due", v: money(totals.balance) },
      ].map((s, i) => (
        <div key={i} className="border border-white/10 bg-[#0a0a0a] p-6">
          <p className="text-xs uppercase tracking-widest text-white/40">{s.l}</p>
          <p className="font-serif text-2xl mt-2 text-white">{s.v}</p>
        </div>
      ))}
    </div>
    {job.stages?.length > 0 && (
      <div className="mt-8">
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-6">Work Timeline</h3>
        <div className="relative pl-8">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10" />
          <div className="space-y-6">
            {job.stages.map((s) => {
              const Icon = STAGE_ICON[s.status] || Circle;
              return (
                <div key={s.id} className="relative">
                  <Icon size={16} className={`absolute -left-8 top-1 ${STAGE_COLOR[s.status] || "text-white/30"}`} />
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="font-serif text-xl">{s.title}</h4>
                    {s.date && <span className="text-xs text-white/40 font-body">{s.date}</span>}
                  </div>
                  {s.description && <p className="mt-1 text-white/55 font-body text-sm">{s.description}</p>}
                  {s.video_url && <a href={s.video_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-gold text-sm hover:text-white"><PlayCircle size={15} /> Watch video</a>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )}
  </div>
);

/* ---------- Gallery ---------- */
const Gallery = ({ job, onOpen }) => {
  const [phase, setPhase] = useState("before");
  const photos = (job.photos || []).filter((p) => p.phase === phase);
  const phases = [{ k: "before", l: "Before" }, { k: "during", l: "During" }, { k: "after", l: "After" }];
  return (
    <div>
      <SectionTitle>Photo Gallery</SectionTitle>
      <div className="flex gap-2 mb-8">
        {phases.map((ph) => {
          const count = (job.photos || []).filter((p) => p.phase === ph.k).length;
          return (
            <button key={ph.k} data-testid={`gallery-phase-${ph.k}`} onClick={() => setPhase(ph.k)}
              className={`px-5 py-2.5 text-xs uppercase tracking-widest border transition-colors ${phase === ph.k ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:text-white"}`}>
              {ph.l} ({count})
            </button>
          );
        })}
      </div>
      {photos.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((ph) => (
            <button key={ph.id} onClick={() => onOpen(ph.url)} className="group block overflow-hidden border border-white/10 aspect-[4/3]">
              <img src={ph.url} alt={ph.caption || ""} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </button>
          ))}
        </div>
      ) : <Empty>No photos in this phase yet.</Empty>}
    </div>
  );
};

/* ---------- Calendar ---------- */
const Schedule = ({ job }) => {
  const items = useMemo(() => {
    const ev = (job.events || []).map((e) => ({ ...e, kind: "Work" }));
    const insp = (job.inspections || []).map((i) => ({ id: i.id, title: i.title, date: i.date, note: i.notes, kind: "Inspection" }));
    return [...ev, ...insp].filter((x) => x.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [job]);
  const fmt = (d) => {
    const dt = new Date(d + "T00:00:00");
    return { m: dt.toLocaleString("en-US", { month: "short" }), d: dt.getDate(), w: dt.toLocaleString("en-US", { weekday: "long" }) };
  };
  return (
    <div>
      <SectionTitle>Schedule</SectionTitle>
      {items.length ? (
        <div className="space-y-3">
          {items.map((it) => {
            const f = fmt(it.date);
            const upcoming = new Date(it.date) >= new Date(new Date().toDateString());
            return (
              <div key={it.id} data-testid={`event-${it.id}`} className={`flex items-center gap-5 border p-4 ${upcoming ? "border-gold/40 bg-[#0a0a0a]" : "border-white/10 opacity-70"}`}>
                <div className="text-center w-14 shrink-0">
                  <p className="text-[10px] uppercase tracking-widest text-gold">{f.m}</p>
                  <p className="font-serif text-3xl leading-none">{f.d}</p>
                </div>
                <div className="flex-1">
                  <p className="font-body text-white">{it.title}</p>
                  <p className="text-white/40 text-sm">{f.w}{it.note ? ` · ${it.note}` : ""}</p>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-white/50 border border-white/15 px-2 py-1 flex items-center gap-1">
                  <CalendarClock size={12} className="text-gold" /> {it.kind}
                </span>
              </div>
            );
          })}
        </div>
      ) : <Empty>No scheduled dates yet.</Empty>}
    </div>
  );
};

/* ---------- Documents ---------- */
const Documents = ({ job }) => {
  const docs = job.documents || [];
  return (
    <div>
      <SectionTitle>Documents</SectionTitle>
      {docs.length ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {docs.map((d) => {
            const Icon = DOC_ICON[d.category] || FileText;
            return (
              <a key={d.id} data-testid={`doc-${d.id}`} href={d.url} target="_blank" rel="noreferrer" download
                className="group flex items-center gap-4 border border-white/10 bg-[#0a0a0a] p-5 hover:border-gold transition-colors">
                <Icon size={26} className="text-gold shrink-0" strokeWidth={1.4} />
                <div className="flex-1 min-w-0">
                  <p className="font-body text-white truncate">{d.name}</p>
                  <p className="text-white/40 text-xs uppercase tracking-widest">{d.category}</p>
                </div>
                <Download size={18} className="text-white/40 group-hover:text-gold transition-colors" />
              </a>
            );
          })}
        </div>
      ) : <Empty>No documents available yet.</Empty>}
    </div>
  );
};

/* ---------- Payments ---------- */
const Payments = ({ job, totals }) => {
  const pct = totals.total > 0 ? Math.min(100, Math.round((totals.paid / totals.total) * 100)) : 0;
  return (
    <div>
      <SectionTitle>Payments</SectionTitle>
      <div className="border border-white/10 bg-[#0a0a0a] p-8">
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <div><p className="text-xs uppercase tracking-widest text-white/40">Contract Total</p><p className="font-serif text-3xl mt-1">{money(totals.total)}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-white/40">Paid</p><p className="font-serif text-3xl mt-1 text-gold">{money(totals.paid)}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-white/40">Balance Due</p><p className="font-serif text-3xl mt-1">{money(totals.balance)}</p></div>
        </div>
        <div className="h-2 bg-white/10 overflow-hidden mb-2">
          <motion.div className="h-full bg-gold" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1 }} />
        </div>
        <p className="text-xs text-white/40">{pct}% paid</p>
      </div>
      <div className="mt-4 space-y-3">
        {(job.payments || []).map((pm) => (
          <div key={pm.id} data-testid={`payment-${pm.id}`} className="flex items-center justify-between border border-white/10 p-4">
            <div>
              <p className="font-body text-white">{pm.label}</p>
              {pm.date && <p className="text-white/40 text-sm">{pm.date}</p>}
            </div>
            <div className="flex items-center gap-4">
              <span className="font-serif text-xl">{money(pm.amount)}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${pm.status === "paid" ? "border-gold/50 text-gold" : "border-white/20 text-white/50"}`}>{pm.status}</span>
            </div>
          </div>
        ))}
        {!(job.payments || []).length && <Empty>No payments recorded yet.</Empty>}
      </div>
    </div>
  );
};

/* ---------- Messages ---------- */
const Messages = ({ job, portalToken, onSent }) => {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const msgs = job.messages || [];
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/portal/job/messages`, { text }, { headers: { Authorization: `Bearer ${portalToken}` } });
      setText("");
      await onSent();
    } catch {
      toast.error("Could not send message");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex flex-col h-full">
      <SectionTitle>Messages</SectionTitle>
      <div className="flex-1 border border-white/10 bg-[#0a0a0a] p-6 space-y-4 overflow-y-auto min-h-[300px] max-h-[50vh]">
        {msgs.length ? msgs.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] p-4 ${m.sender === "client" ? "bg-gold text-black" : "bg-white/5 border border-white/10 text-white"}`}>
              <p className="text-[10px] uppercase tracking-widest mb-1 opacity-70">{m.sender === "client" ? "You" : "GI Remodeling"}</p>
              <p className="font-body text-sm whitespace-pre-wrap">{m.text}</p>
            </div>
          </div>
        )) : <Empty>No messages yet. Start the conversation below.</Empty>}
      </div>
      <form onSubmit={send} className="flex gap-3 mt-4">
        <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message…" className="editorial-input flex-1" />
        <button data-testid="message-send" disabled={sending} className="bg-gold text-black px-6 hover:bg-white transition-colors flex items-center gap-2 text-xs uppercase tracking-widest disabled:opacity-50">
          <Send size={15} /> Send
        </button>
      </form>
    </div>
  );
};

/* ---------- Satisfaction Bar (client rating) ---------- */
const RATING_LABELS = [
  { max: 20, label: "Not satisfied", color: "text-red-400" },
  { max: 45, label: "Could be better", color: "text-orange-400" },
  { max: 70, label: "Satisfied", color: "text-yellow-400" },
  { max: 90, label: "Very satisfied", color: "text-gold" },
  { max: 100, label: "Excellent", color: "text-gold" },
];
const ratingMeta = (v) => RATING_LABELS.find((r) => v <= r.max) || RATING_LABELS[0];

const SatisfactionBar = ({ job, portalToken, onSaved }) => {
  const initial = typeof job.satisfaction_rating === "number" ? job.satisfaction_rating : 80;
  const [value, setValue] = useState(initial);
  const [comment, setComment] = useState(job.satisfaction_comment || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(job.satisfaction_updated_at || null);
  const meta = ratingMeta(value);

  useEffect(() => {
    setValue(typeof job.satisfaction_rating === "number" ? job.satisfaction_rating : 80);
    setComment(job.satisfaction_comment || "");
    setSavedAt(job.satisfaction_updated_at || null);
  }, [job.id, job.satisfaction_rating, job.satisfaction_comment, job.satisfaction_updated_at]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        "/portal/job/satisfaction",
        { rating: value, comment },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setSavedAt(data.updated_at);
      toast.success("Thanks for your feedback!");
      onSaved?.();
    } catch {
      toast.error("Could not save your rating");
    } finally {
      setSaving(false);
    }
  };

  const savedLabel = savedAt ? new Date(savedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;

  return (
    <div data-testid="satisfaction-bar" className="mt-12 border-t border-white/10 pt-10">
      <div className="border border-gold/25 bg-gradient-to-br from-[#0d0b06] to-[#0a0a0a] p-6 md:p-8">
        <div className="flex items-baseline justify-between gap-4 mb-6 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Your feedback</p>
            <h3 className="font-serif text-2xl md:text-3xl mt-2">How satisfied are you with the work?</h3>
            <p className="text-white/50 text-sm mt-2 max-w-lg">Slide to let GI Remodeling know how the project is going. You can update this anytime.</p>
          </div>
          <div className="text-right">
            <p className={`font-serif text-4xl md:text-5xl ${meta.color}`} data-testid="satisfaction-value">{value}<span className="text-white/30 text-2xl">/100</span></p>
            <p className={`text-xs uppercase tracking-widest mt-1 ${meta.color}`}>{meta.label}</p>
          </div>
        </div>

        <div className="relative">
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="satisfaction-slider w-full"
            data-testid="satisfaction-slider"
            aria-label="Satisfaction rating"
            style={{
              background: `linear-gradient(to right, #d4af37 0%, #d4af37 ${value}%, rgba(255,255,255,0.12) ${value}%, rgba(255,255,255,0.12) 100%)`,
            }}
          />
          <div className="flex justify-between mt-3 text-[10px] uppercase tracking-widest text-white/40">
            <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">Optional comment</label>
          <textarea
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us what's going well or what could be improved…"
            className="editorial-input resize-none"
            data-testid="satisfaction-comment"
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-white/40 text-xs">{savedLabel ? `Last updated ${savedLabel}` : "Not submitted yet"}</p>
          <button
            onClick={save}
            disabled={saving}
            data-testid="satisfaction-submit"
            className="bg-gold text-black text-xs uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : savedLabel ? "Update rating" : "Submit rating"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Project Hero (subtle personalized cover) ---------- */
const ProjectHero = ({ job }) => {
  // Pick the best available photo: cover_image > last "after" > last "during" > last "before" > first
  const photos = job.photos || [];
  const pickPhase = (phase) => {
    const list = photos.filter((p) => p.phase === phase);
    return list.length ? list[list.length - 1].url : null;
  };
  const coverPhoto =
    job.cover_image ||
    pickPhase("after") ||
    pickPhase("during") ||
    pickPhase("before") ||
    (photos[photos.length - 1] && photos[photos.length - 1].url) ||
    null;

  return (
    <section data-testid="project-hero" className="relative h-[36vh] min-h-[260px] max-h-[380px] w-full overflow-hidden border-b border-white/10">
      {coverPhoto ? (
        <img src={coverPhoto} alt={job.title} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        // Elegant fallback while photos are being added
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.15),transparent_60%),linear-gradient(to_bottom_right,#0d0d0d,#050505_60%,#1a1503)]">
          <div className="absolute right-0 bottom-0 font-serif text-[16rem] leading-none opacity-[0.05] select-none pr-8">
            {(job.title || "GI").charAt(0)}
          </div>
        </div>
      )}
      {/* Subtle overlay — darker at bottom for text legibility, minimal on top */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
      <div className="relative z-10 h-full max-w-[1300px] mx-auto flex flex-col justify-end px-5 md:px-10 pb-6 md:pb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold/90 mb-2">Your Project</p>
        <h1 className="font-serif font-light text-3xl md:text-4xl leading-tight max-w-3xl">
          {job.title || "Project"}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-white/70 font-body text-xs md:text-sm">
          {job.location && (
            <span className="inline-flex items-center gap-1.5" data-testid="project-hero-location">
              <MapPin size={13} className="text-gold" /> {job.location}
            </span>
          )}
          {typeof job.progress === "number" && (
            <span className="inline-flex items-center gap-2 text-white/80">
              <span className="w-14 h-[3px] bg-white/15 relative overflow-hidden">
                <span className="absolute inset-y-0 left-0 bg-gold" style={{ width: `${job.progress}%` }} />
              </span>
              {job.progress}% complete
            </span>
          )}
          <span className="text-white/50 uppercase tracking-widest text-[10px] border border-white/15 px-2 py-0.5">
            {STATUS_LABEL[job.status] || job.status}
          </span>
        </div>
      </div>
    </section>
  );
};

/* ================= Dashboard ================= */
const NAV = [
  { k: "overview", l: "Overview", icon: LayoutDashboard },
  { k: "gallery", l: "Gallery", icon: Images },
  { k: "schedule", l: "Schedule", icon: CalendarDays },
  { k: "documents", l: "Documents", icon: FileText },
  { k: "payments", l: "Payments", icon: CreditCard },
  { k: "messages", l: "Messages", icon: MessageSquare },
];

const Dashboard = ({ portalToken, onLogout }) => {
  const [job, setJob] = useState(null);
  const [section, setSection] = useState("overview");
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/job", { headers: { Authorization: `Bearer ${portalToken}` } });
      setJob(data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        // Silent re-auth using stored code — client never sees "session expired"
        const fresh = await silentReauth();
        if (fresh) {
          try {
            const { data } = await api.get("/portal/job", { headers: { Authorization: `Bearer ${fresh}` } });
            setJob(data);
            setError(null);
            return;
          } catch {}
        }
        // Silently clear stale credentials — no confusing toast on cold-start.
        // If the token expired mid-session, the user will simply see the code entry screen again.
        onLogout();
      } else {
        setError(errText(err.response?.data?.detail, "Could not load project"));
      }
    }
  }, [portalToken, onLogout]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    if (!job) return { total: 0, paid: 0, balance: 0 };
    const total = Number(job.contract_total || 0);
    const paid = (job.payments || []).filter((x) => x.status === "paid").reduce((s, x) => s + Number(x.amount || 0), 0);
    const pendingSum = (job.payments || []).filter((x) => x.status !== "paid").reduce((s, x) => s + Number(x.amount || 0), 0);
    const balance = total > 0 ? Math.max(total - paid, 0) : pendingSum;
    return { total: total || paid + pendingSum, paid, balance };
  }, [job]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-white/60 mb-6">{error}</p>
          <button onClick={onLogout} className="bg-gold text-black text-xs uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors">Back to code entry</button>
        </div>
      </div>
    );
  }

  if (!job) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-gold font-serif text-xl">…</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 px-4 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur-xl z-50">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="GI" className="h-11 w-11 object-contain" />
          <span className="font-serif text-lg hidden sm:block">My Project</span>
        </Link>
        <div className="flex items-center gap-4">
          <button data-testid="portal-logout" onClick={onLogout} className="flex items-center gap-2 text-white/60 hover:text-white text-sm">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto grid lg:grid-cols-[240px_1fr]">
        <aside className="lg:border-r border-white/10 lg:min-h-[calc(100vh-80px)]">
          <div className="hidden lg:block p-6">
            <p className="text-gold text-xs uppercase tracking-[0.25em]">Welcome,</p>
            <p className="font-serif text-2xl mb-2">{job.client_name}</p>
            <p className="text-white/40 text-xs uppercase tracking-widest">Project #{(job.invitation_code || "").replace("GI-", "")}</p>
          </div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible px-3 py-3 lg:px-3 border-b lg:border-b-0 border-white/10">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = section === n.k;
              return (
                <button key={n.k} data-testid={`portal-nav-${n.k}`} onClick={() => setSection(n.k)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition-colors ${active ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white border-l-2 border-transparent"}`}>
                  <Icon size={17} /> {n.l}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="p-0">
          <ProjectHero job={job} />
          <div className="p-5 md:p-10">
            <AnimatePresence mode="wait">
              <motion.div key={section} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                {section === "overview" && (
                  <>
                    <Overview job={job} totals={totals} />
                    <SatisfactionBar job={job} portalToken={portalToken} onSaved={load} />
                  </>
                )}
                {section === "gallery" && <Gallery job={job} onOpen={setLightbox} />}
                {section === "schedule" && <Schedule job={job} />}
                {section === "documents" && <Documents job={job} />}
                {section === "payments" && <Payments job={job} totals={totals} />}
                {section === "messages" && <Messages job={job} portalToken={portalToken} onSent={load} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6" data-testid="portal-lightbox">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={28} /></button>
            <motion.img initial={{ scale: 0.95 }} animate={{ scale: 1 }} src={lightbox} alt="" className="max-h-[85vh] max-w-[90vw] object-contain border border-white/10" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Portal() {
  const [portalToken, setPortalToken] = useState(null);
  const [checking, setChecking] = useState(true);

  // On mount, silently validate any stored token BEFORE showing the dashboard.
  // If invalid (or if silent reauth fails), we clear localStorage without any error toast
  // and drop the user on the clean code-entry screen. Clients never see confusing messages.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = localStorage.getItem(PORTAL_TOKEN_KEY);
      if (!stored) {
        if (!cancelled) setChecking(false);
        return;
      }
      try {
        await api.get("/portal/job", { headers: { Authorization: `Bearer ${stored}` } });
        if (!cancelled) setPortalToken(stored);
      } catch {
        // Try silent reauth using stored code (if any)
        const fresh = await silentReauth();
        if (!cancelled) {
          if (fresh) {
            setPortalToken(fresh);
          } else {
            // Clean any stale data silently — no toast, no confusion.
            localStorage.removeItem(PORTAL_TOKEN_KEY);
            localStorage.removeItem(PORTAL_CODE_KEY);
          }
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    localStorage.removeItem(PORTAL_CODE_KEY);
    setPortalToken(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="font-serif text-2xl">
          <span className="gold-gradient-text">GI</span>{" "}
          <span className="silver-gradient-text">Remodeling</span>
          <span className="text-white/40 ml-3 animate-pulse">…</span>
        </div>
      </div>
    );
  }
  if (!portalToken) return <CodeScreen onAuthed={setPortalToken} />;
  return <Dashboard portalToken={portalToken} onLogout={handleLogout} />;
}
